import * as React from "react";

import Config from "../Config";
import Commons from "../utils/Commons";
import EntitiesTree from "./EntitiesTree";
import RequestHelper from "../utils/RequestHelper";
import JsonTreeDataCreator from "../utils/JsonTreeDataCreator";
import EntitiesList from "./EntitiesList";
import StateGlobal from "../StateGlobal";

export default class RepoEntitiesList extends EntitiesList {
    constructor(props) {
        super(props);
        this.state = {
            selected: {
                models: [],
                objects: [],
                queries: [],
            },
            loading: false,
            searchModels: "",  // Added state for search strings
            searchObjects: "",
            searchQueries: "",
        };
    }

    updateObjectsSelection = (aSelected) => {
        this.setState((prevState) => ({
            selected: { ...prevState.selected, objects: aSelected },
        }));
    };

    updateModelsSelection = (aSelected) => {
        this.setState((prevState) => ({
            selected: { ...prevState.selected, models: aSelected },
        }));
    };

    updateQueriesSelection = (aSelected) => {
        this.setState((prevState) => ({
            selected: { ...prevState.selected, queries: aSelected },
        }));
    };

    componentDidMount() {
        if (
            !Config.aEntities.objects ||
            !Config.aEntities.models ||
            !Config.aEntities.queries ||
            StateGlobal.isRepoEntitiesNotActual()
        ) {
            this.loadEntities();
            StateGlobal.resetRepoEntitiesNotActual();
        }
    }

    loadEntities = async () => {
        this.setState({ loading: true });
        let aObjectsGroup = null;
        let aModelsGroup = null;
        let aQueries = null;
        try {
            if (!Config.aAnpRepo.id) {
                throw new Error(
                    "Repository ID is missing. Please select proper repository in Report Creator configuration.!"
                );
            }

            const productVersion = Config.getProductName();
            let includeRWF = "";
            if (productVersion === "ADONIS") {
                includeRWF = "&attribute=MFB_RWF_VERSION";
            }

            await RequestHelper.sendRequestSimple({
                request: Commons.createGatewayRequestUrl({
                    url: Config.anpRequestUrl.getObjectGroup
                        .replace(/%repo_id%/g, Config.aAnpRepo.id)
                        .replace(/%group_id%/g, "root")
                        .concat(includeRWF),
                    dataType: "json",
                }),
                dataType: "json",
            }).then((aResponse) => {
                if (aResponse && aResponse.isErr) {
                    console.error(aResponse.message);
                    throw new Error(aResponse.message);
                }
                if (!aResponse.hasOwnProperty("group")) {
                    throw new Error("Cannot find any entity in root group!");
                }
                aObjectsGroup = aResponse;
            });

            await RequestHelper.sendRequestSimple({
                request: Commons.createGatewayRequestUrl({
                    url: Config.anpRequestUrl.getModelGroup
                        .replace(/%repo_id%/g, Config.aAnpRepo.id)
                        .replace(/%group_id%/g, "root")
                        .concat(includeRWF),
                    dataType: "json",
                }),
                dataType: "json",
            }).then((aResponse) => {
                if (aResponse && aResponse.isErr) {
                    console.error(aResponse.message);
                    throw new Error(aResponse.message);
                }
                if (!aResponse.hasOwnProperty("group")) {
                    throw new Error("Cannot find any entity in root group!");
                }
                aModelsGroup = aResponse;
            });

            await RequestHelper.sendRequestSimple({
                request: Commons.createGatewayRequestUrl({
                    url: Config.anpRequestUrl.getQueries.replace(
                        /%repo_id%/g,
                        Config.aAnpRepo.id
                    ),
                    dataType: "json",
                }),
                dataType: "json",
            }).then((aResponse) => {
                if (aResponse && aResponse.isErr) {
                    console.error(aResponse.message);
                    throw new Error(aResponse.message);
                }
                if (!aResponse.hasOwnProperty("items")) {
                    throw new Error("Cannot find any search query item!");
                }
                aQueries = aResponse;
            });

            Config.setEntities({
                models: aModelsGroup,
                objects: aObjectsGroup,
                queries: aQueries,
            });
        } catch (aEx) {
            console.error(aEx);
            Config.setEntities({
                queries: {
                    items: null,
                },
                models: {
                    group: null,
                },
                objects: {
                    group: null,
                },
            });
        } finally {
            this.setState({ loading: false });
        }
    };

    // New function to filter the tree data based on a search string
    filterTreeData(treeData, searchString) {
        function filterNode(node) {
            let filteredNode = {};

            for (let key in node) {
                if (key.toLowerCase().includes(searchString.toLowerCase())) {
                    filteredNode[key] = node[key];
                } else if (
                    typeof node[key] === "object" &&
                    !Array.isArray(node[key])
                ) {
                    let childNode = filterNode(node[key]);
                    if (Object.keys(childNode).length > 0) {
                        filteredNode[key] = childNode;
                    }
                }
            }
            return filteredNode;
        }

        return filterNode(treeData);
    }

    // Handle search input changes for models
    handleSearchModelsChange = (event) => {
        this.setState({ searchModels: event.target.value });
    };

    // Handle search input changes for objects
    handleSearchObjectsChange = (event) => {
        this.setState({ searchObjects: event.target.value });
    };

    // Handle search input changes for queries
    handleSearchQueriesChange = (event) => {
        this.setState({ searchQueries: event.target.value });
    };

    render() {
        const aPreSelected = {
            models:
                this.props.selected &&
                this.props.selected.hasOwnProperty("models") &&
                Array.isArray(this.props.selected.models)
                    ? this.props.selected.models
                    : [],
            objects:
                this.props.selected &&
                this.props.selected.hasOwnProperty("objects") &&
                Array.isArray(this.props.selected.objects)
                    ? this.props.selected.objects
                    : [],
            queries:
                this.props.selected &&
                this.props.selected.hasOwnProperty("queries") &&
                Array.isArray(this.props.selected.queries)
                    ? this.props.selected.queries
                    : [],
        };

        const { searchModels, searchObjects, searchQueries, loading } =
            this.state;

        const filteredModelsTreeData = this.filterTreeData(
            JsonTreeDataCreator.entitiesGroupTree(
                Config.aEntities.models,
                false,
                false
            ),
            searchModels
        );

        const filteredObjectsTreeData = this.filterTreeData(
            JsonTreeDataCreator.entitiesGroupTree(
                Config.aEntities.objects,
                false,
                false
            ),
            searchObjects
        );

        const filteredQueriesTreeData = this.filterTreeData(
            JsonTreeDataCreator.queriesTree(Config.aEntities.queries),
            searchQueries
        );

        return (
            <div>
                {loading ? (
                    <div style={{ marginBottom: "20px" }}>
                        <h3 className="App__infoMessage">
                            {Config.strings.messages.entitiesList.loading}
                        </h3>
                    </div>
                ) : (
                    <div>
                        <div>
                        <h3 className="list-header">{Config.strings.tabs.create.models}</h3>
                        <div className="search-container">
                            <input
                            className="search-input"
                            type="text"
                            placeholder={Config.strings.jsonTree.search.searchInput.searchModelPlaceholder}
                            value={searchModels}
                            onChange={this.handleSearchModelsChange}
                            />
                        </div>
                        {Config.aEntities?.models?.group && (
                            <EntitiesTree
                            key={`Models_${searchModels}`} // Add searchModels to the key
                            toggle={this.state.searchModels.length > 0}
                            treeData={filteredModelsTreeData}
                            multiSelect={true}
                            selectionCallback={this.updateModelsSelection}
                            selected={aPreSelected.models ? aPreSelected.models : []}
                            />
                        )}
                        <span className="search-empty-category">
                            {Object.keys(filteredModelsTreeData).length === 0 &&
                            Config.strings.jsonTree.search.searchInput.searchEmptyCategory}
                        </span>
                        </div>
                        <div>
                        <h3 className="list-header">{Config.strings.tabs.create.objects}</h3>
                        <div className="search-container">
                            <input
                            className="search-input"
                            type="text"
                            placeholder={Config.strings.jsonTree.search.searchInput.searchObjectPlaceholder}
                            value={searchObjects}
                            onChange={this.handleSearchObjectsChange}
                            />
                        </div>
                        {Config.aEntities?.objects?.group && (
                            <EntitiesTree
                            key={`Objects_${searchObjects}`} // Add searchObjects to the key
                            toggle={this.state.searchObjects.length > 0}
                            treeData={filteredObjectsTreeData}
                            multiSelect={true}
                            selectionCallback={this.updateObjectsSelection}
                            selected={aPreSelected.objects ? aPreSelected.objects : []}
                            />
                        )}
                        <span className="search-empty-category">
                            {Object.keys(filteredObjectsTreeData).length === 0 &&
                            Config.strings.jsonTree.search.searchInput.searchEmptyCategory}
                        </span>
                        </div>
                        <div>
                        <h3 className="list-header">{Config.strings.tabs.create.queries}</h3>
                        <div className="search-container">
                            <input
                            className="search-input"
                            type="text"
                            placeholder={Config.strings.jsonTree.search.searchInput.searchQueriesPlaceholder}
                            value={searchQueries}
                            onChange={this.handleSearchQueriesChange}
                            />
                        </div>
                        {Config.aEntities?.queries?.items && (
                            <EntitiesTree
                            key={`Queries_${searchQueries}`} // Add searchQueries to the key
                            toggle={this.state.searchQueries.length > 0}
                            treeData={filteredQueriesTreeData}
                            multiSelect={true}
                            selectionCallback={this.updateQueriesSelection}
                            selected={aPreSelected.queries ? aPreSelected.queries : []}
                            />
                        )}
                        <span className="search-empty-category">
                            {Object.keys(filteredQueriesTreeData).length === 0 &&
                            Config.strings.jsonTree.search.searchInput.searchEmptyCategory}
                        </span>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
